import React, { useState } from "react";
import styles from "./QuestionCard.module.css";
import clsx from "clsx";
import {
  KnowledgeStatus,
  KnowledgeCard,
} from "../../../../../../../api/responsePayload/KnowledgeResponse";
import { ColorTagDetails } from "../../../../../../../util/ExampleData";
import ActionButtons from "../actionButton/ActionButtons";
import CategorySection from "../categorySection/CategorySection";
import ChatHistoryButton from "../chatHistoryButton/ChatHistoryButton";
import Metadata from "../metaData/Metadata";
import QuestionAnswerSection from "../questionAnswerSection/QuestionAnswerSection";
import SubcategorySection from "../subcategorySection/SubcategorySection";
import { KnowledgeType } from "../../../../../../../util/KnowledgeType";
import DocumentSection from "../documentSection/DocumentSection";
/* eslint-disable complexity */

const getStatusStyles = (
  status: KnowledgeStatus,
  checked: boolean,
  categoryColor: ColorTagDetails | undefined
) => {
  return {
    [KnowledgeStatus.NeedReview]: categoryColor,
    [KnowledgeStatus.PreApproved]: checked
      ? "badge-color-positive"
      : "badge-color-default",
    [KnowledgeStatus.Rejected]: checked
      ? "badge-color-negative"
      : "badge-color-default",
    [KnowledgeStatus.Approved]: checked
      ? "badge-color-positive"
      : "badge-color-default",
  }[status];
};

export interface QuestionCardProps extends KnowledgeCard {
  openScreenshotSelector: (knowledge_id: number) => void;
  onExitEditModeClicked: (knowledge_id: number, isEditing: boolean) => void;
  onSaveClicked: (knowledge_id: number) => void;
  handleRemoveScreenshotClicked?: (imageId: number) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  id,
  dateTime,
  conversationId,
  category,
  subcategories,
  type,
  question,
  answer,
  content,
  status,
  isSelected = false,
  context,
  knowledge_content_image,
  openScreenshotSelector,
  onExitEditModeClicked,
  onSaveClicked,
  handleRemoveScreenshotClicked,
}) => {
  const [isEditSelected, setEditSelected] = useState(false);
  const [updatedQuestion, setUpdatedQuestion] = useState(question);
  const [updatedAnswer, setUpdatedAnswer] = useState(answer);
  const [updateContent, setUpdatedContent] = useState(content);

  const categoryColor: ColorTagDetails = category?.colorDetails
    ? category?.colorDetails
    : { borderColor: "#fff", lightColor: "#fff", darkColor: "#000" };

  const statusStyle = getStatusStyles(status, isSelected, categoryColor);

  const handleEditChange = (
    updatedQuestion?: string | null,
    updatedAnswer?: string
  ) => {
    if (type == KnowledgeType.FAQ) {
      setUpdatedAnswer(updatedAnswer ?? "");
      setUpdatedQuestion(updatedQuestion ?? "");
    } else if (type == KnowledgeType.DOCUMENT) {
      setUpdatedContent(updatedQuestion ?? "");
    }
  };

  return (
    <div className={clsx(styles["question-group-container"])}>
      <div
        className={clsx(
          styles["question-group-main"],
          isEditSelected && styles["qc-editing-mode"],
          statusStyle
        )}
        style={
          status === KnowledgeStatus.NeedReview
            ? {
                backgroundColor: categoryColor?.lightColor,
                borderColor: categoryColor?.borderColor,
                color: categoryColor?.darkColor,
              }
            : undefined
        }
      >
        <div className={styles["question-container"]}>
          <Metadata
            date={dateTime}
            time={dateTime}
            text={`ConversationId: ${conversationId}`}
          />
          {type == KnowledgeType.FAQ && (
            <>
              {context && context && (
                <ChatHistoryButton conversationData={context} />
              )}
              <CategorySection
                category={category ? category.name : ""}
                color={categoryColor}
              />
              {subcategories && (
                <SubcategorySection subcategories={subcategories} />
              )}
              <QuestionAnswerSection
                question={question}
                answer={answer}
                knowledge_content_image={knowledge_content_image}
                isEditing={isEditSelected}
                onChange={handleEditChange}
                color={categoryColor.lightColor}
                openSelector={() => openScreenshotSelector(id)}
                handleRemoveScreenshotClicked={handleRemoveScreenshotClicked}
              />
            </>
          )}
          {type == KnowledgeType.DOCUMENT && (
            <>
              <DocumentSection
                content={content ?? ""}
                isEditing={isEditSelected}
                onChange={handleEditChange}
                color={categoryColor.lightColor}
              />
            </>
          )}

          <div
            className={clsx(
              styles["question-button-actions"],
              (status !== KnowledgeStatus.NeedReview || isEditSelected) &&
                styles["preapproved"]
            )}
          >
            {
              <ActionButtons
                status={status}
                isEditSelected={isEditSelected}
                setEditSelected={setEditSelected}
                id={id}
                type={type}
                onExitEditModeClicked={() =>
                  onExitEditModeClicked(id, isEditSelected)
                }
                onSaveClicked={() => onSaveClicked(id)}
                updatedQuestion={updatedQuestion}
                updatedAnswer={updatedAnswer}
                updateContent={updateContent}
              />
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionCard;
